type LiveChatInit = {
  init: (options: InitOptions) => Promise<void>;
};

type InitOptions = {
  brandCode?: string;
  marketCode?: string;
  languageCode?: string;
  environment: 'int' | 'prod';
};

async function createAssetLoaderScript(path: string): Promise<void> {
  return new Promise((resolve) => {
    const assetLoaderScript = document.createElement('script');
    assetLoaderScript.src = '/livechat' + path;
    assetLoaderScript.dataset.sourceMap = path + '.map';
    assetLoaderScript.type = 'module';
    assetLoaderScript.onload = () => resolve();
    document.head.appendChild(assetLoaderScript);
  });
}

async function fetchResource(url: string, errorMessage: string): Promise<void | Response> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status} ${response.statusText}}`);
    }

    return response;
  } catch (e: unknown) {
    if (e instanceof Error) {
      console.error(errorMessage, e);
    }
    // gracefully ignore fetches as we get a lot of junk exceptions from bots and WAF'ed requests
    // these rejections are monitored elsewhere - (ELCelerate and DataDog)
    return;
  }
}

export const LiveChatInit: LiveChatInit = {
  async init({ brandCode, marketCode, languageCode, environment }: InitOptions): Promise<void> {
    const useConfigSystem = !!(brandCode && marketCode && languageCode);
    window.LiveChat = {
      ...window.LiveChat,
      useConfigSystem,
      brandCode,
      environment
    };

    try {
      if (useConfigSystem) {
        await initWithConfigSystem({ brandCode, marketCode, languageCode, environment });
      } else {
        throw new Error('Could not initiate LiveChat - insufficient parameters');
      }
    } catch (error: unknown) {
      const errorMessage = `LIVECHAT ERROR: ${(error as Error).message}`;
      console.error(errorMessage);
      window.Sentry?.captureException(errorMessage);
    }
  }
};

async function initWithConfigSystem({
  brandCode,
  marketCode,
  languageCode,
  environment
}: Required<Pick<InitOptions, 'brandCode' | 'marketCode' | 'languageCode' | 'environment'>>) {
  const configUrl = `/livechat/${environment}/config/${brandCode}-${marketCode}-${languageCode}.json`;
  const configErrorMessage = 'Error fetching LiveChat config from Akamai NetStorage';
  const configResponse = await fetchResource(configUrl, configErrorMessage);
  if (!configResponse) return;

  const config = (await configResponse.json()) as Resources;

  const coreManifestUrl = `/livechat/${environment}/manifests/core-manifest.json`;
  const coreManifestErrorMessage = 'Error fetching LiveChat core manifest';

  const coreManifestResponse = await fetchResource(coreManifestUrl, coreManifestErrorMessage);
  if (!coreManifestResponse) return;

  const coreManifest = (await coreManifestResponse.json()) as Manifest;
  window.LiveChat!.resources = config;
  window.LiveChat!.manifest = coreManifest;

  await createAssetLoaderScript(`/${environment}${coreManifest.asset_loader[0]}`);
}
